import * as React from "react";
import { Ramp } from "../../../../../types";
import { Shape } from "../../RampEditor/RampCanvas/RampCanvas";
import { LayerPanelPresenter } from "./LayerPanelPresenter";
import { useLayerOperations } from "./useLayerOperations";

export type LayerPanelContainerProps = {
  ramp: Ramp;
  setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => void;
  activeShapes: string[];
  setActiveShapes: (shapeIds: string[]) => void;
  highlightedShapes: string[];
  onClose: () => void;
  onCenterShape?: (shapeId: string) => void;
  canvasWidth?: number;
};

/**
 * Container component for LayerPanel.
 *
 * Responsibilities:
 * - Receives ramp data from parent
 * - Creates operations via useLayerOperations hook
 * - Passes data + operations to presenter
 *
 * Does NOT contain any UI rendering or local UI state.
 */
export const LayerPanelContainer: React.FC<LayerPanelContainerProps> = ({
  ramp,
  setRamp,
  activeShapes,
  setActiveShapes,
  highlightedShapes,
  onClose,
  onCenterShape,
  canvasWidth = 600,
}) => {
  const operations = useLayerOperations(ramp, setRamp);

  // Callback for PropertiesSection to update shapes directly
  const setShapes = React.useCallback(
    (newShapes: Shape[]) => {
      setRamp((prev) => ({ ...prev, shapes: newShapes }));
    },
    [setRamp]
  );

  return (
    <LayerPanelPresenter
      // Data (read-only from container's perspective)
      shapes={ramp.shapes || []}
      groups={ramp.layer_groups || []}
      activeShapes={activeShapes}
      highlightedShapes={highlightedShapes}
      // Operations (from hook)
      operations={operations}
      // Direct shapes setter for PropertiesSection
      setShapes={setShapes}
      // Selection management (passed through)
      setActiveShapes={setActiveShapes}
      // UI callbacks
      onClose={onClose}
      onCenterShape={onCenterShape}
      canvasWidth={canvasWidth}
    />
  );
};

// Re-export the container as the default LayerPanel
export { LayerPanelContainer as LayerPanel };
